import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { $$toast } from '@doger/ui/components/ui/toast';
import type { SessionResponse } from '@doger/core/shared/api';
import { $$api } from '@doger/core/shared/api';

import { useAnalytics } from '@doger/chat/shared/analytics';

import { useTranslation } from '../../../shared/i18n';

type useDeleteChatMutationParams = {
  chatId: string;
  isActive: boolean;
};

export const useDeleteChatMutation = ({
  chatId,
  isActive,
}: useDeleteChatMutationParams) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation('app');
  const analytics = useAnalytics();

  return useMutation({
    mutationKey: ['auth', 'sessions', 'delete', chatId],
    mutationFn: (chatId: string) => {
      const isDeletingActiveChat = isActive;

      if (isDeletingActiveChat) {
        navigate('/');
      }

      return $$api.deleteSession({
        sessionId: chatId,
      });
    },

    onMutate: (chatId) => {
      // Optimistically update the cache for instant feedback
      queryClient.setQueryData(
        ['auth', 'sessions'],
        (oldData: SessionResponse[]) => {
          if (!oldData) return oldData;

          return oldData.filter((session) => session.id !== chatId);
        }
      );
    },

    onSuccess: (_, chatId) => {
      analytics.amplitude.track({
        event: 'User Deleted Chat',
        properties: { chatId },
      });

      queryClient.setQueryData(
        ['auth', 'sessions'],
        (oldData: SessionResponse[]) => {
          if (!oldData) return oldData;

          return oldData.filter((session) => session.id !== chatId);
        }
      );

      queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] });
    },

    onError: (error) => {
      console.error('Failed to delete AI session:', error);
      $$toast.error(t('errors.chat.failedToDelete'));
    },
  });
};
